// packages/modules/src/fetch.ts
import 'pinia';
import qs from 'qs';
import defaultConfig from './api';
// ------------------
// API Implementation
// ------------------
const API = (client) => ({
    /**
     * The core function to make an API call.
     * @param key The key of the API endpoint in the config.
     * @param args Arguments to pass to the config function.
     * @returns A promise that resolves with the API response.
     */
    async call(key, ...args) {
        if (!(key in client.config)) {
            throw new Error(`No API config found for key ${key}`);
        }
        const cfg = client.config[key](...args);
        const controller = new AbortController();
        setTimeout(() => controller.abort(), 20_000);
        const params = qs.stringify({ ...client.params, ...cfg.params }, { arrayFormat: 'brackets' });
        const url = new URL(`${client.origin}${cfg.url}${params ? `?${params}` : ''}`);
        const headers = new Headers(client.headers);
        if (cfg.headers) {
            const cfgHeaders = new Headers(cfg.headers);
            cfgHeaders.forEach((v, k) => headers.set(k, v));
        }
        let body;
        if (cfg.data) {
            if (cfg.data instanceof FormData) {
                body = cfg.data;
                // For FormData, we must let the browser set the Content-Type header
                // because it needs to include the boundary string.
                headers.delete('Content-Type');
            }
            else if (cfg.data instanceof Blob || cfg.data instanceof File) {
                body = cfg.data;
                // If it's a Blob/File, we should ensure Content-Type is set if provided in cfg.headers
                // otherwise fetch will set it if possible.
                if (!headers.has('Content-Type') && cfg.data.type) {
                    headers.set('Content-Type', cfg.data.type);
                }
            }
            else {
                body = JSON.stringify(cfg.data);
                if (!headers.has('Content-Type')) {
                    headers.set('Content-Type', 'application/json');
                }
            }
        }
        const requestOptions = {
            method: cfg.method || 'GET',
            headers,
            body,
            signal: controller.signal,
            ...cfg.options,
        };
        try {
            const response = await fetch(url.toString(), requestOptions);
            // The 'x-krakend-completed' header check from the original axios implementation.
            const krakendCompleted = response.headers.get('x-krakend-completed');
            if (cfg.requireComplete && krakendCompleted && krakendCompleted !== 'true') {
                throw new Error('Incomplete response from backend gateway (KrakenD).');
            }
            if (!response.ok) {
                const errorText = await response.text();
                // eslint-disable-next-line no-console
                // console.error(`API call failed for key "${key}": ${response.status} ${response.statusText}`, errorText, requestOptions, url)
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
            const responseData = await response.json().catch(() => ({})); // Gracefully handle empty/invalid JSON
            return {
                data: responseData,
                status: response.status,
                headers: response.headers,
            };
        }
        catch (error) {
            // eslint-disable-next-line no-console
            // console.error(`Error during API call for key "${key}":`, error, requestOptions, url)
            throw error;
        }
    },
    /**
     * Abort functionality is now handled per-call via AbortController.
     * This function is kept for interface compatibility but is a no-op.
     */
    abort() {
        // No-op. Aborting is handled by the signal on each call.
    },
});
/**
 * Creates a new, isolated API client instance.
 * This is the factory function that should be used to create clients for each request on the server.
 * @param options Configuration for the API client.
 * @returns A new API instance.
 */
export function createAPI(options) {
    const api = API({
        origin: options.origin,
        config: { ...defaultConfig, ...options.config },
        headers: options.headers,
        params: options.params,
    });
    return api;
}
// ------------------
// Vue Plugin
// ------------------
export const plugin = {
    install(app, api) {
        // The original plugin also provided $axios, which is no longer relevant.
        app.config.globalProperties.$api = api;
    },
};
